import React, { useState, useEffect } from 'react';
import { useIntl, getLocale } from 'umi';
import { Card, Button, Checkbox } from 'tea-component';
import SelectMultiple from '@/pages/intelligence/pages/Compare/PcConsoleGames/components/SelectMultiple';
import RegionSelector from '@/pages/intelligence/components/RegionSelector';
import { isArrayValueSame, addFormatMessageSafelyHandler } from '../../../../utils';
import {
  REGION_SELECTOR_RADIOS,
  PERIODS_METRICS_CONFIG,
  DEVICES_NAMES_MAPPING,
  SEGMENTS_NAMES_MAPPING,
  SEGMENTS_NAMES_MAPPING_KEY,
  SEGMENTS_NAMES_MAPPING_TIP
} from '../../../../constants';
import type { IBaseProps, IRegionType, IRegionValue } from '@/pages/intelligence/types/common';
import type { IFilter, IRegion } from '@/pages/intelligence/types/pcConsoleCompare';

const { DEVICES, SEGMENTS } = PERIODS_METRICS_CONFIG.DIGITAL_REVENUE;

const DEVICES_OPTIONS = DEVICES.map((item) => ({
  text: DEVICES_NAMES_MAPPING?.[item] || item || '',
  value: item,
}));

const SEGMENTS_OPTIONS = SEGMENTS.map((item) => {
  return {
    text: SEGMENTS_NAMES_MAPPING?.[item] || item || '',
    value: item,
    key: SEGMENTS_NAMES_MAPPING_KEY?.[item] || item || '',
    tooltipKey: SEGMENTS_NAMES_MAPPING_TIP?.[item] || item || '',
  }
});

interface IFilterProps extends IBaseProps {
  metrics: string[];
  metricsOptions: { text: string; value: string }[];
  regionsCountries: IRegion[];
  filter: IFilter;
  onChange: (payload: IFilter, isTablesNeedReReq: boolean, isTrendsNeedReReq: boolean) => void;
}

export default function Filter(props: IFilterProps) {
  const {
    metrics,
    metricsOptions: metricsOptionsOuter,
    regionsCountries = [],
    filter: filterOutter,
    onChange: onChangeOutter,
    handleReset: handleResetOutter,
    entityType
  } = props;

  const { formatMessage } = useIntl();
  const locale = getLocale();
  const formatMessageSafely = addFormatMessageSafelyHandler(formatMessage);

  const metricsOptions = metricsOptionsOuter?.map((metricOption) => {
    const text = formatMessageSafely({ id: metricOption?.text });
    return {
      ...metricOption,
      text,
    };
  });

  const [filter, setFilter] = useState<IFilter>({
    metrics: [...metrics],
    devices: [...DEVICES],
    segments: [...SEGMENTS],
    regionData: {
      type: 'market',
      regions: [],
      markets: ['global'],
    },
    isHeatMap: false,
    isShowTop3Countries: false,
  });

  useEffect(() => {
    setFilter({
      ...filter,
      ...filterOutter,
    });
  }, [filterOutter]);

  const handleMetricsChange = (payload: string[]) => {
    const filterTemp = {
      ...filter,
      metrics: Array.isArray(payload) ? payload : [...metrics],
    };
    setFilter(filterTemp);
  };

  const handleDevicesChange = (payload: string[]) => {
    const filterTemp = {
      ...filter,
      devices: Array.isArray(payload) ? payload : [],
    };
    setFilter(filterTemp);
  };

  const handleSegmentsChange = (payload: string[]) => {
    const filterTemp = {
      ...filter,
      segments: Array.isArray(payload) ? payload : [],
    };
    setFilter(filterTemp);
  };

  const handleRegionCountryChange = (value: { type: IRegionType; regions: IRegionValue; markets: IRegionValue }) => {
    const regionDataTemp = {
      ...filter?.regionData,
      type: value?.type || 'market',
      regions: value?.regions || [],
      markets: value?.markets || [],
    };
    const filterTemp = {
      ...filter,
      regionData: regionDataTemp,
    };
    setFilter(filterTemp);
  };

  const handleIsHeatMapChange = (payload: boolean) => {
    const filterTemp = {
      ...filter,
      isHeatMap: payload,
    };
    setFilter(filterTemp);
  };

  const handleIsShowTop3CountriesChange = (payload: boolean) => {
    const filterTemp = {
      ...filter,
      isShowTop3Countries: payload,
    };
    setFilter(filterTemp);
  };

  const handleConfirm = (e?: React.MouseEvent<Element, MouseEvent>) => {
    e?.stopPropagation();
    e?.preventDefault();
    // 数据项筛选，这些条件变化，肯定需要重新请求 对标表格、折线图、指标累计值数据
    const isDataFilterChange =
      !isArrayValueSame(filter?.devices, filterOutter?.devices) ||
      !isArrayValueSame(filter?.segments, filterOutter?.segments) ||
      filter?.regionData?.type !== filterOutter?.regionData?.type ||
      !isArrayValueSame(filter?.regionData?.regions, filterOutter?.regionData?.regions) ||
      !isArrayValueSame(filter?.regionData?.markets, filterOutter?.regionData?.markets) ||
      !isArrayValueSame(filter?.metrics, filterOutter?.metrics);
    // 所有筛选条件都没变化，直接点确定，需要重新请求 对标表格、折线图、指标累计值数据
    const isNeedsReReq =
      !isDataFilterChange &&
      filterOutter?.isHeatMap === filter?.isHeatMap &&
      filterOutter?.isShowTop3Countries === filter?.isShowTop3Countries;
    // 是否需要重新请求 对比表格的数据
    const isTablesNeedReReq =
      isNeedsReReq || isDataFilterChange || (!filterOutter?.isShowTop3Countries && !!filter?.isShowTop3Countries);
    // 是否需要重新请求 折线图、指标累计值的数据
    const isTrendsNeedReReq = isNeedsReReq || isDataFilterChange;
    onChangeOutter?.(filter, isTablesNeedReReq, isTrendsNeedReReq);
  };

  const handleReset = (e?: React.MouseEvent<Element, MouseEvent>) => {
    e?.stopPropagation();
    e?.preventDefault();
    setFilter({
      ...filter,
      metrics: [...metrics],
      devices: [...DEVICES],
      segments: [...SEGMENTS],
      regionData: {
        type: 'market',
        regions: [],
        markets: ['global'],
      },
      isHeatMap: false,
      isShowTop3Countries: false,
    });
    handleResetOutter?.()
  };

  return (
    <Card className="dbi-pc-console-compare_filter">
      <Card.Body>
        <div className="dbi-filter">
          <SelectMultiple
            label={formatMessage({ id: 'dbi_compare_label_device' })}
            // 此处使用解构赋值的方式解决初次渲染结果没有全选的问题
            className="dbi-pc-console-compare_filter-control-item search-item"
            options={DEVICES_OPTIONS}
            value={filter?.devices}
            onChange={handleDevicesChange}
          />
          <SelectMultiple
            label={formatMessage({ id: 'dbi_filter_segment' })}
            // 此处使用解构赋值的方式解决初次渲染结果没有全选的问题
            className="dbi-pc-console-compare_filter-control-item search-item"
            options={SEGMENTS_OPTIONS}
            value={filter?.segments}
            onChange={handleSegmentsChange}
          />
          <RegionSelector
            key="dbi-pc-console-compare-region"
            className="dbi-pc-console-compare_filter-control-item search-item dbi-pc-console-compare_region-selector"
            locale={locale}
            // popularList={[]}
            radioOptions={REGION_SELECTOR_RADIOS}
            options={regionsCountries}
            value={{
              ...filter?.regionData,
              extendFilterType: 'common',
            }}
            entityType={entityType}
            onConfirm={handleRegionCountryChange}
            // onCloseReginSelect={(value) => {
            //   console.log('[ value ] >', value);
            // }}
          />
          <SelectMultiple
            label={formatMessage({ id: 'dbi_label_metrics' })}
            // 此处使用解构赋值的方式解决初次渲染结果没有全选的问题
            className="dbi-pc-console-compare_filter-control-other search-item"
            style={{ marginRight: 20 }}
            size='l'
            options={metricsOptions ?? []}
            value={filter?.metrics}
            onChange={handleMetricsChange}
          />
           <Checkbox value={filter?.isHeatMap} onChange={handleIsHeatMapChange} style={{marginBottom:15}}>
              {formatMessage({ id: 'dbi_compare_heat_map' })}
            </Checkbox>
            <Checkbox value={filter?.isShowTop3Countries} onChange={handleIsShowTop3CountriesChange} style={{marginBottom:15}}>
              {formatMessage({ id: 'dbi_compare_show_top_3_countries' })}
            </Checkbox>
          {/* <div className="dbi-filter-checkbox-container search-item">

          </div> */}
        </div>
        <div className="dbi-filter-btn">
          <Button type="primary" className="dbi-filter-btn-lf" onClick={handleConfirm}>
            {formatMessage({ id: 'dbi_confirm' })}
          </Button>
          <Button type="weak" onClick={handleReset}>
            {formatMessage({ id: 'dbi_reset' })}
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
}
